/**
 * useApplyTheme — syncs the V2 UI store to <html> data-* attributes.
 *
 * CRITICAL (PRP Gotcha #1): the V2 theme tokens are defined under
 * `:root[data-theme="..."]` selectors. When V2 is active we set
 * data-theme / data-accent / data-density on <html> plus the density
 * row height. When V2 is OFF we remove them all so the classic `:root`
 * tokens (which redefine `--accent` as an HSL triplet) are restored and
 * the classic UI renders pixel-identical to before.
 */

import { useLayoutEffect } from 'react';
import { useUiStore, type V2Density } from './uiStore';
import { BRANDS } from '../branding/registry';

/** Maps density choice to the `--row-h` CSS variable value. */
export const DENSITY_H: Record<V2Density, string> = {
  compact: '34px',
  regular: '38px',
  comfy: '44px',
};

/**
 * Applies (or clears) the V2 theme attributes on the document root.
 *
 * Call once high in the React tree (e.g. in App). Keyed on
 * v2Enabled/theme/accent/density so it re-runs on any change.
 *
 * @param enabledOverride - When provided, used instead of the store's
 *   `v2Enabled`. The app passes `v2Enabled && !isClassicShellRoute(pathname)`
 *   so admin/rule screens render in the classic palette (attributes stripped)
 *   without persisting the global V2-off toggle. Defaults to the store value.
 * @param classicDark - When true, adds the `.dark` class to <html> so the
 *   classic shell's dark token block (index.css) activates. The app passes
 *   `isClassicShellRoute(pathname) && v2Enabled && theme==='dark'` so the
 *   admin/rule/review screens render dark IN PARITY with the rest of the site,
 *   without touching the V2 `data-theme` tokens (different variable set).
 *   Safe globally — nothing in the app uses Tailwind `dark:` variants. B-615.
 * @returns Nothing; operates via side effects on document.documentElement.
 */
export function useApplyTheme(enabledOverride?: boolean, classicDark = false): void {
  const storeV2Enabled = useUiStore((s) => s.v2Enabled);
  const v2Enabled = enabledOverride ?? storeV2Enabled;
  const theme = useUiStore((s) => s.theme);
  const accent = useUiStore((s) => s.accent);
  const density = useUiStore((s) => s.density);
  const brand = useUiStore((s) => s.brand);

  // useLayoutEffect (not useEffect) so the strip/apply lands before paint —
  // otherwise navigating from a V2 screen to /admin flashes one frame of V2
  // tokens on the classic page.
  useLayoutEffect(() => {
    // Classic-shell dark: activate the `.dark` token block (index.css) so the
    // admin/rule/review screens go dark alongside the V2 chrome. Kept separate
    // from the V2 data-* tokens below because the two palettes use different
    // variable names/formats (V2 hex vs classic shadcn HSL-triplet). B-615.
    document.documentElement.classList.toggle('dark', classicDark);

    const el = document.documentElement;
    if (v2Enabled) {
      const activeBrand = brand || 'standard';
      const def = BRANDS[activeBrand];

      // Coerce the theme to one the active brand supports (e.g. Citizens is
      // light-only). The brand's CSS overrides the palette anyway, but keeping
      // data-theme consistent makes the statefarm[data-theme=light] block and
      // any theme-conditional component CSS resolve correctly.
      const effectiveTheme = def.supportedThemes.includes(theme)
        ? theme
        : def.defaultTheme ?? def.supportedThemes[0];

      el.dataset.theme = effectiveTheme;
      el.dataset.accent = accent; // brand tokens override --accent when locked
      el.dataset.density = density;
      el.dataset.brand = activeBrand;
      el.style.setProperty('--row-h', DENSITY_H[density]);
    } else {
      delete el.dataset.theme;
      delete el.dataset.accent;
      delete el.dataset.density;
      delete el.dataset.brand;
      el.style.removeProperty('--row-h');
    }
  }, [v2Enabled, theme, accent, density, brand, classicDark]);
}
